import type {
    ComponentStageData,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    PluginHeuristicsRegistry,
    PluginStageHandler
} from '../../types'
import {widthHeuristics, horizontalMarginsHeuristics, getAvailableWidthKey} from './heuristicsDefinitions'
import {findHeuristic, getParent, createAdaptiveHeuristic} from '../common'
import type {Pointer, UnitSize} from '@wix/document-services-types'
import {getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import type {LayoutEntryItem} from '../../conversionDal/types'
import type {HorizontalScaleAndSizePluginAPI} from './types'

export const createPlugin = ({stages}: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const updateAvailableWidthByExistingLayout = (
        {componentPointer, conversionDal, config}: ComponentStageData,
        contextHelper?: PluginContext
    ) => {
        const parentPointer = getParent(conversionDal, componentPointer)
        const availableWidthKey = getAvailableWidthKey(componentPointer)

        if (!parentPointer) {
            return contextHelper!.set(availableWidthKey, config.targetCanvasSize)
        }

        contextHelper!.set(
            availableWidthKey,
            ((
                (conversionDal.get(getTargetLayoutPointer(componentPointer)) as LayoutEntryItem).componentLayout
                    .width as UnitSize
            ).value /
                100) *
                contextHelper!.get(getAvailableWidthKey(parentPointer))
        )
    }

    const run: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData,
        contextHelper?: PluginContext
    ) => {
        const marginsHeuristicToApply = findHeuristic(horizontalMarginsHeuristics, componentStageData)
        marginsHeuristicToApply.apply(componentStageData, contextHelper)
        const widthHeuristicToApply = findHeuristic(widthHeuristics, componentStageData)
        widthHeuristicToApply.apply(componentStageData, contextHelper)

        return {
            appliedHeuristics: [marginsHeuristicToApply.heuristicCode, widthHeuristicToApply.heuristicCode]
        }
    }

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(
            stages.HORIZONTAL_LAYOUT_SCALE,
            createAdaptiveHeuristic(run, updateAvailableWidthByExistingLayout)
        )
    }

    const createApi = (context: PluginContext): HorizontalScaleAndSizePluginAPI => {
        return {
            getWidth: (compPointer: Pointer): number => context.get(getAvailableWidthKey(compPointer))
        }
    }

    const name = 'horizontalScaleAndSize'

    return {register, createApi, name, dependencies: []}
}
